import { Command } from 'commander';
import chalk from 'chalk';
import fs from 'fs-extra';
import { join, dirname, basename } from 'path';
import { execSync } from 'child_process';
import { logger } from '../lib/logger.js';
import { findApplicableMigrations } from '../migrations/registry.js';
import type { MigrationResult, MigrationSummary } from '../migrations/types.js';
import { glob } from 'glob';
import { addMissingKeys, removeObsoleteKeys, relocateLegacyKeys, resolveTemplatePlaceholders } from '../lib/config-sync.js';
import { purgeRalphProjectArtifacts } from '../lib/installer.js';
import { configureClaudeEnvironment, removeMcpServerRegistration } from '../lib/vscode-settings.js';
import { REQUIRED_PACKAGES, resolveLockedFamilyVersion } from '../lib/required-packages.js';
import {
  MIGRATION_IGNORE_GLOBS,
  computeUpgradeOutcome,
  insertExportDatasetsSeam,
  EXPORT_DATASETS_BEGIN,
  stripPlatformAdminFallbacks,
  classifyAdminModuleFallback,
  ADMIN_FALLBACK_SPECIFIER,
} from '../lib/upgrade-helpers.js';
import { parseNuGetDowngrades } from '../lib/nuget-downgrade.js';
import { getLatestNpmVersion } from '../lib/npm-registry.js';
import { syncDockerFiles, logDockerFileResult, logSkippedModifiedSummary } from '../lib/docker-sync.js';
import { loadInitState, saveInitState, createCompletedInitState } from '../lib/file-tracker.js';

interface UpgradeOptions {
  preview?: boolean;
  dryRun?: boolean;
  allPackages?: boolean;
  forceDocker?: boolean;
}

interface SmartStackConfig {
  projectType: string;
  dbContext: string;
  baseNamespace: string;
  smartStackVersion: string;
  [key: string]: unknown;
}

interface ProjectUpgradeInfo {
  csprojPath: string;
  relPath: string;
  currentVersion: string | null;
  /** Cached .csproj content to avoid re-reads */
  csprojContent: string;
}

interface PackageInfo {
  name: string;
  currentVersion: string;
}

interface UpgradeResult {
  nugetUpgraded: number;
  nugetSkipped: number;
  nugetFailed: number;
  otherPkgUpdated: number;
  otherPkgSkipped: number;
  otherPkgFailed: number;
  npmUpgraded: boolean;
  npmSkipped: boolean;
  npmOtherUpdated: boolean;
  configSynced: number;
  claudeSettingsSynced: number;
  ralphPurged: boolean;
  dockerSynced: number;
  /** Docker files left untouched because the client modified them locally. */
  dockerSkippedModified: string[];
  testFrontendSynced: number;
  /** Locale bundles recovered from the phantom `web/<app>-web` tree (pre-5.11 scaffold-component bug). */
  i18nBundlesRelocated: number;
  /** Platform-admin fallback registrations / orphan placeholder removed (aggregator ≤ 5.12). */
  adminFallbacksCleaned: number;
  residualsCleaned: number;
  initStateFixed: boolean;
  requiredPkgAdded: number;
  requiredPkgFailed: number;
  /** True when a `dotnet restore` failed — the .csproj was edited but won't restore. */
  restoreFailed: boolean;
  /** Direct references auto-bumped to clear an NU1605 downgrade (e.g. EF Core 10.0.5 → 10.0.7). */
  packagesAligned: number;
  /** Packages NuGet still reports as downgrades after auto-repair — feeds the remediation hint. */
  unresolvedDowngrades: string[];
  programCsIssues: string[];
}

const TEMPLATES_DIR = join(dirname(__dirname), 'templates', 'project');

/**
 * Fetches the latest version of a NuGet package from nuget.org.
 * When prerelease=true, returns the latest prerelease version.
 * When prerelease=false, returns the latest stable version,
 * falling back to prerelease if no stable version exists.
 */
async function getLatestNuGetVersion(packageName: string, prerelease: boolean): Promise<string | null> {
  try {
    const response = await fetch(`https://api.nuget.org/v3-flatcontainer/${packageName.toLowerCase()}/index.json`);

    if (!response.ok) {
      logger.warning(`Could not fetch versions for ${packageName} from NuGet`);
      return null;
    }

    const data = await response.json() as { versions: string[] };
    const versions = data.versions;

    if (!versions || versions.length === 0) {
      return null;
    }

    if (prerelease) {
      // --preview: return the latest prerelease version
      const prereleaseVersions = versions.filter(v => v.includes('-'));
      if (prereleaseVersions.length > 0) {
        return prereleaseVersions[prereleaseVersions.length - 1];
      }
      // Fallback: return the absolute latest version
      return versions[versions.length - 1];
    }

    // Default: return the latest stable version
    const stableVersions = versions.filter(v => !v.includes('-'));
    if (stableVersions.length > 0) {
      return stableVersions[stableVersions.length - 1];
    }

    // Fallback: no stable versions exist, return latest prerelease with warning
    logger.warning(`No stable version found for ${packageName}, using latest prerelease`);
    return versions[versions.length - 1];
  } catch (error) {
    logger.warning(`Error fetching latest version for ${packageName}: ${error instanceof Error ? error.message : error}`);
    return null;
  }
}

/**
 * Detects if current directory is a SmartStack project.
 */
async function detectSmartStackProject(): Promise<{ isProject: boolean; config?: SmartStackConfig; projectDir: string }> {
  const cwd = process.cwd();
  const configPath = join(cwd, '.smartstack', 'config.json');

  // Primary: use .smartstack/config.json
  if (await fs.pathExists(configPath)) {
    try {
      const config = await fs.readJson(configPath) as SmartStackConfig;
      return { isProject: true, config, projectDir: cwd };
    } catch (error) {
      logger.warning('Could not read .smartstack/config.json');
    }
  }

  // Fallback: detect by scanning src/ for .csproj referencing SmartStack
  const srcDir = join(cwd, 'src');
  if (await fs.pathExists(srcDir)) {
    const folders = await fs.readdir(srcDir);
    for (const folder of folders) {
      const folderPath = join(srcDir, folder);
      const stat = await fs.stat(folderPath);
      if (!stat.isDirectory()) continue;

      const csprojFiles = (await fs.readdir(folderPath)).filter(f => f.endsWith('.csproj'));
      for (const csproj of csprojFiles) {
        const content = await fs.readFile(join(folderPath, csproj), 'utf-8');
        const version = readPackageVersion(content, 'SmartStack');
        if (version) {
          // Found SmartStack reference — infer baseNamespace from folder name
          // e.g., "SmartstackApp.Api" → "SmartstackApp"
          const dotIndex = folder.lastIndexOf('.');
          const baseNamespace = dotIndex > 0 ? folder.substring(0, dotIndex) : folder;

          logger.warning('No .smartstack/config.json found — detected SmartStack project from .csproj files');
          logger.info(`Tip: Run ${chalk.cyan('ss init --here')} to create the config file.`);

          return {
            isProject: true,
            config: {
              projectType: 'fullstack',
              dbContext: `${baseNamespace}DbContext`,
              baseNamespace,
              smartStackVersion: version,
            },
            projectDir: cwd,
          };
        }
      }
    }
  }

  return { isProject: false, projectDir: cwd };
}

/**
 * Reads the current SmartStack package version from a .csproj file.
 * Returns null if not found.
 */
function readPackageVersion(csprojContent: string, packageName: string): string | null {
  // Match: <PackageReference Include="SmartStack" Version="2.0.0" />
  // or:    <PackageReference Include="SmartStack" Version="2.0.0-preview.380" />
  const regex = new RegExp(
    `<PackageReference\\s+Include="${packageName}"\\s+Version="([^"]+)"`,
    'i'
  );
  const match = csprojContent.match(regex);
  return match ? match[1] : null;
}

/**
 * Reads ALL PackageReferences from a .csproj file.
 */
function readAllPackageReferences(csprojContent: string): PackageInfo[] {
  const regex = /<PackageReference\s+Include="([^"]+)"\s+Version="([^"]+)"/gi;
  const packages: PackageInfo[] = [];
  let match;
  while ((match = regex.exec(csprojContent)) !== null) {
    packages.push({ name: match[1], currentVersion: match[2] });
  }
  return packages;
}

/**
 * Finds all .csproj files in the src/ directory.
 * Returns all projects regardless of whether they reference SmartStack.
 */
async function findAllProjects(projectDir: string): Promise<ProjectUpgradeInfo[]> {
  const srcDir = join(projectDir, 'src');
  if (!await fs.pathExists(srcDir)) {
    return [];
  }

  const projects: ProjectUpgradeInfo[] = [];
  const folders = await fs.readdir(srcDir);

  for (const folder of folders) {
    const folderPath = join(srcDir, folder);
    const stat = await fs.stat(folderPath);

    if (stat.isDirectory()) {
      const csprojFiles = (await fs.readdir(folderPath)).filter(f => f.endsWith('.csproj'));

      for (const csproj of csprojFiles) {
        const csprojPath = join(folderPath, csproj);
        const content = await fs.readFile(csprojPath, 'utf-8');

        // Read SmartStack version if present (null if not referenced)
        const currentVersion = readPackageVersion(content, 'SmartStack');
        projects.push({
          csprojPath,
          relPath: csprojPath.replace(projectDir, '').replace(/^[/\\]/, ''),
          currentVersion,
          csprojContent: content,
        });
      }
    }
  }

  return projects;
}

/**
 * Reads the current @atlashub/smartstack version from package.json.
 */
async function readFrontendVersion(frontendDir: string): Promise<string | null> {
  try {
    const packageJsonPath = join(frontendDir, 'package.json');
    const packageJson = await fs.readJson(packageJsonPath);
    const version = packageJson.dependencies?.['@atlashub/smartstack'];
    // version could be a tag like "next", "latest", or a semver like "2.0.0"
    if (!version || version === 'latest' || version === 'next') {
      return null; // Can't determine exact version from tags
    }
    // Strip leading ^ or ~ if present
    return version.replace(/^[\^~]/, '');
  } catch {
    return null;
  }
}

/**
 * Finds the frontend directory.
 */
async function findFrontendDirectory(projectDir: string): Promise<string | null> {
  const webDir = join(projectDir, 'web');
  if (!await fs.pathExists(webDir)) {
    return null;
  }

  const folders = await fs.readdir(webDir);
  for (const folder of folders) {
    const packageJsonPath = join(webDir, folder, 'package.json');
    if (await fs.pathExists(packageJsonPath)) {
      const packageJson = await fs.readJson(packageJsonPath);
      // Check if it has @atlashub/smartstack dependency
      if (packageJson.dependencies?.['@atlashub/smartstack']) {
        return join(webDir, folder);
      }
    }
  }

  return null;
}

function execCommand(command: string, cwd?: string, dryRun = false): void {
  if (dryRun) {
    logger.info(`[DRY RUN] ${command}`);
    return;
  }

  try {
    execSync(command, { cwd, stdio: 'inherit' });
  } catch (error) {
    throw new Error(`Command failed: ${command}`);
  }
}

/**
 * Tries to execute a command and returns success status instead of throwing
 */
function tryExecCommand(command: string, cwd?: string, dryRun = false): boolean {
  if (dryRun) {
    logger.info(`[DRY RUN] ${command}`);
    return true;
  }

  try {
    execSync(command, { cwd, stdio: 'inherit' });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Runs a command, ALWAYS echoes its combined output to the console (so the user still
 * sees NuGet errors exactly as before), and returns the captured text so callers can
 * parse it — needed for NU1605 auto-repair, which `stdio:'inherit'` cannot support.
 */
function execCapture(command: string, cwd?: string): { ok: boolean; output: string } {
  try {
    const out = execSync(command, { cwd, encoding: 'utf-8', stdio: ['ignore', 'pipe', 'pipe'] });
    if (out) process.stdout.write(out);
    return { ok: true, output: out ?? '' };
  } catch (error) {
    const e = error as { stdout?: string | Buffer; stderr?: string | Buffer };
    const output = `${e.stdout?.toString() ?? ''}${e.stderr?.toString() ?? ''}`;
    if (output) process.stdout.write(output);
    return { ok: false, output };
  }
}

/**
 * After a failed `dotnet restore`, auto-aligns direct PackageReferences that NuGet
 * flagged as downgrades (NU1605) — the recurring failure when SmartStack raises a
 * transitive floor (e.g. EF Core 10.0.5 → 10.0.7) past a version the `.csproj` still
 * pins directly. Each conflicting direct reference is bumped UP to the required floor
 * (NuGet's own prescribed fix), then the solution is re-restored. A bounded loop
 * absorbs cascades where one bump surfaces the next conflict.
 *
 * Returns whether the restore ultimately succeeds, the alignments performed, and any
 * downgrades still unresolved (different/unfixable conflicts) for the remediation hint.
 */
async function repairNuGetDowngrades(
  firstFailureOutput: string,
  allProjects: ProjectUpgradeInfo[],
  projectDir: string,
  dryRun: boolean,
): Promise<{ resolved: boolean; aligned: string[]; unresolved: string[] }> {
  const aligned: string[] = [];
  let lastOutput = firstFailureOutput;
  const MAX_PASSES = 3;

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const downgrades = parseNuGetDowngrades(lastOutput);
    if (downgrades.length === 0) break; // nothing (more) we can auto-fix

    let bumpedAny = false;
    for (const d of downgrades) {
      const project = allProjects.find(
        (p) => basename(p.csprojPath).replace(/\.csproj$/i, '') === d.project,
      );
      if (!project) {
        logger.warning(`  ${chalk.yellow('?')} NU1605 on ${d.package} in unrecognised project ${d.project} — left for manual fix`);
        continue;
      }

      const cmd = `dotnet add "${project.csprojPath}" package ${d.package} --version ${d.requiredVersion} --no-restore`;
      if (dryRun) {
        logger.info(`  [DRY RUN] would align ${chalk.cyan(d.package)} → ${d.requiredVersion} in ${chalk.cyan(d.project)}`);
        aligned.push(`${d.project}: ${d.package} → ${d.requiredVersion}`);
        bumpedAny = true;
        continue;
      }

      if (execCapture(cmd).ok) {
        logger.success(`  ${chalk.green('↑')} Aligned ${chalk.cyan(d.package)} → ${chalk.cyan(d.requiredVersion)} in ${chalk.cyan(d.project)} (was downgrading a transitive dependency)`);
        aligned.push(`${d.project}: ${d.package} → ${d.requiredVersion}`);
        bumpedAny = true;
      } else {
        logger.error(`  ${chalk.red('✗')} Could not set ${d.package} ${d.requiredVersion} in ${d.project}`);
      }
    }

    if (!bumpedAny) break; // could not act on any downgrade — stop
    if (dryRun) return { resolved: false, aligned, unresolved: [] };

    logger.info('Re-restoring after alignment...');
    const restore = execCapture('dotnet restore', projectDir);
    if (restore.ok) return { resolved: true, aligned, unresolved: [] };
    lastOutput = restore.output;
  }

  const unresolved = [...new Set(parseNuGetDowngrades(lastOutput).map((d) => d.package))];
  return { resolved: false, aligned, unresolved };
}

/**
 * Executes code migrations for a project
 */
async function executeMigrations(
  projectDir: string,
  fromVersion: string,
  toVersion: string,
  dryRun: boolean
): Promise<MigrationSummary> {
  const migrations = findApplicableMigrations(fromVersion, toVersion);
  const results: MigrationResult[] = [];

  if (migrations.length === 0) {
    return {
      fromVersion,
      toVersion,
      totalApplied: 0,
      totalSkipped: 0,
      results: [],
      hasErrors: false
    };
  }

  logger.info(`Found ${chalk.cyan(migrations.length)} code migration(s) to apply:`);
  migrations.forEach(m => {
    logger.info(`  • ${m.description} (${m.fromVersion} → ${m.toVersion})`);
  });
  console.log();

  for (const migration of migrations) {
    for (const fileMigration of migration.migrations) {
      // Find files matching the pattern
      const files = await glob(fileMigration.pattern, {
        cwd: projectDir,
        absolute: true,
        nodir: true,
        ignore: MIGRATION_IGNORE_GLOBS,
      });

      for (const file of files) {
        try {
          const content = await fs.readFile(file, 'utf-8');

          // Check if migration is needed
          if (!fileMigration.detect(content, file)) {
            results.push({
              filePath: file.replace(projectDir, '').replace(/^[/\\]/, ''),
              applied: false,
              description: 'Already up to date'
            });
            continue;
          }

          // Apply migration (with .bak backup before destructive overwrite)
          let actuallyApplied = true;
          if (!dryRun) {
            const transformed = fileMigration.transform(content, file);
            if (transformed === content) {
              actuallyApplied = false;
            } else {
              await fs.copyFile(file, `${file}.bak`);
              await fs.writeFile(file, transformed, 'utf-8');
            }
          }

          results.push({
            filePath: file.replace(projectDir, '').replace(/^[/\\]/, ''),
            applied: actuallyApplied,
            description: actuallyApplied ? fileMigration.description : 'No content changes'
          });

          if (actuallyApplied) {
            logger.success(`${dryRun ? '[DRY RUN] Would migrate' : 'Migrated'}: ${file.replace(projectDir, '').replace(/^[/\\]/, '')}`);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          results.push({
            filePath: file.replace(projectDir, '').replace(/^[/\\]/, ''),
            applied: false,
            description: fileMigration.description,
            error: errorMessage
          });

          logger.error(`Failed to migrate ${file.replace(projectDir, '').replace(/^[/\\]/, '')}: ${errorMessage}`);
        }
      }
    }
  }

  const totalApplied = results.filter(r => r.applied).length;
  const totalSkipped = results.filter(r => !r.applied && !r.error).length;
  const hasErrors = results.some(r => r.error !== undefined);

  return {
    fromVersion,
    toVersion,
    totalApplied,
    totalSkipped,
    results,
    hasErrors
  };
}

/**
 * Syncs the client's appsettings.json with the template.
 * Adds missing sections/keys without touching existing values.
 */
/**
 * Validates that Program.cs contains all required SmartStack bootstrap calls.
 * Returns a list of missing calls (empty = all good).
 */
async function validateProgramCs(
  projectDir: string,
  baseNamespace: string,
): Promise<string[]> {
  const programPath = join(projectDir, 'src', `${baseNamespace}.Api`, 'Program.cs');

  if (!await fs.pathExists(programPath)) {
    return ['Program.cs not found'];
  }

  const content = await fs.readFile(programPath, 'utf-8');

  const requiredCalls = [
    { pattern: /\.AddSmartStack\s*\(/, label: 'AddSmartStack()' },
    { pattern: /\.InitializeSmartStackAsync\s*\(/, label: 'InitializeSmartStackAsync()' },
    { pattern: /\.UseSmartStack\s*\(/, label: 'UseSmartStack()' },
    { pattern: /\.MapSmartStack\s*\(/, label: 'MapSmartStack()' },
  ];

  const missing: string[] = [];
  for (const { pattern, label } of requiredCalls) {
    if (!pattern.test(content)) {
      missing.push(label);
    }
  }

  return missing;
}

async function syncAppSettings(
  projectDir: string,
  baseNamespace: string,
  dryRun: boolean,
): Promise<{ added: string[]; removed: string[]; relocated: string[] }> {
  const appSettingsPath = join(projectDir, 'src', `${baseNamespace}.Api`, 'appsettings.json');

  if (!await fs.pathExists(appSettingsPath)) {
    logger.warning('appsettings.json not found, skipping config sync');
    return { added: [], removed: [], relocated: [] };
  }

  const templatePath = join(TEMPLATES_DIR, 'appsettings.json.template');
  if (!await fs.pathExists(templatePath)) {
    logger.warning('appsettings.json.template not found, skipping config sync');
    return { added: [], removed: [], relocated: [] };
  }

  // Load and resolve template
  const rawTemplate = await fs.readFile(templatePath, 'utf-8');
  const resolvedTemplate = resolveTemplatePlaceholders(rawTemplate, baseNamespace);
  const templateJson = JSON.parse(resolvedTemplate) as Record<string, unknown>;

  // Load client's current config
  const clientContent = await fs.readFile(appSettingsPath, 'utf-8');
  const clientJson = JSON.parse(clientContent) as Record<string, unknown>;

  // Reconcile structure: relocate configured legacy sections to their new home,
  // add new template keys, then prune the deprecated/relocated nodes. Relocate
  // runs FIRST so a configured section (e.g. top-level EntraSso) is carried over
  // before removeObsoleteKeys deletes the dead node.
  const relocated = relocateLegacyKeys(clientJson);
  const added = addMissingKeys(clientJson, templateJson);
  const removed = removeObsoleteKeys(clientJson);

  if (added.length === 0 && removed.length === 0 && relocated.length === 0) {
    return { added, removed, relocated };
  }

  if (!dryRun) {
    await fs.writeFile(appSettingsPath, JSON.stringify(clientJson, null, 2));
  }

  return { added, removed, relocated };
}

/**
 * Syncs the project's .claude/settings.json with the template.
 * Adds missing keys/sections without touching existing user values.
 */
async function syncClaudeSettings(
  projectDir: string,
  dryRun: boolean,
): Promise<string[]> {
  const settingsPath = join(projectDir, '.claude', 'settings.json');
  const templatePath = join(TEMPLATES_DIR, 'claude-settings.json.template');

  if (!await fs.pathExists(templatePath)) {
    logger.debug('claude-settings.json.template not found, skipping');
    return [];
  }

  // Load template
  const templateContent = await fs.readFile(templatePath, 'utf-8');
  const templateJson = JSON.parse(templateContent) as Record<string, unknown>;

  // Load or create client's current settings
  let clientJson: Record<string, unknown> = {};
  if (await fs.pathExists(settingsPath)) {
    const clientContent = await fs.readFile(settingsPath, 'utf-8');
    clientJson = JSON.parse(clientContent) as Record<string, unknown>;
  }

  // Find and add missing keys (never delete existing)
  const added = addMissingKeys(clientJson, templateJson);

  if (added.length === 0) {
    return [];
  }

  if (!dryRun) {
    await fs.ensureDir(join(projectDir, '.claude'));
    await fs.writeFile(settingsPath, JSON.stringify(clientJson, null, 2) + '\n');
  }

  return added;
}

/**
 * Ensures `scripts/patch-smartstack-theme.cjs` exists in the frontend project and
 * that the upstream theme.css bug is patched right now (if node_modules is already
 * populated). Returns the number of files written (0 or 1).
 *
 * The package.json `postinstall` hook is added by the matching CodeMigration
 * (smartstack-theme-postinstall-migration); this function only handles the
 * provisioning of the script file itself and the immediate in-place patch.
 */
/**
 * Registers the project's client extension entities for the platform global search by invoking the
 * deterministic `scaffold-extension-search` discovery CLI. Idempotent and FAIL-SOFT — any problem here
 * is logged and skipped, it never breaks the upgrade. Row-scope-suspect entities are NOT auto-registered;
 * they are reported for manual review so search can never reveal rows their list hides.
 */
async function syncExtensionSearchRegistration(
  projectDir: string,
  appCode: string,
  dryRun: boolean,
): Promise<void> {
  // Reuse the proven TEMPLATES_DIR base; the skills tree is a sibling of templates/project.
  const cli = join(dirname(TEMPLATES_DIR), 'skills', 'development', 'backend', 'data-layer', 'cli', 'scaffold-extension-search', 'index.ts');
  if (!await fs.pathExists(cli)) {
    logger.debug('scaffold-extension-search CLI not found, skipping global-search registration');
    return;
  }

  // Only relevant for projects that actually declare client extension entities.
  const ctxFiles = await glob('**/ExtensionsDbContext.cs', {
    cwd: join(projectDir, 'src'),
    ignore: ['**/bin/**', '**/obj/**'],
  });
  if (ctxFiles.length === 0) {
    logger.debug('No ExtensionsDbContext found, skipping global-search registration');
    return;
  }

  logger.info('Registering client entities for global search...');
  try {
    const flags = `--discover --project "${projectDir}" --app "${appCode}"${dryRun ? ' --dry-run' : ''}`;
    const out = execSync(`npx --prefer-offline tsx "${cli}" ${flags}`, {
      cwd: dirname(dirname(TEMPLATES_DIR)),
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    const env = JSON.parse(out) as {
      data?: { registered?: string[]; review?: Array<{ kind: string; entity?: string; detail: string }> };
    };
    const registered = env.data?.registered ?? [];
    const review = env.data?.review ?? [];
    const suspects = review.filter((r) => r.kind === 'row-scope-suspect');

    if (registered.length > 0) {
      logger.success(`${dryRun ? '[DRY RUN] Would register' : 'Registered'} ${registered.length} search categor${registered.length === 1 ? 'y' : 'ies'}: ${registered.join(', ')}`);
    } else {
      logger.info(`Global search ${chalk.green('✓')} (nothing new to auto-register)`);
    }
    for (const s of suspects) {
      logger.warning(`[REVIEW] "${s.entity}" not auto-registered (row-scope) — add .RestrictTo(...) by hand. ${s.detail}`);
    }
    const unbound = review.filter((r) => r.kind !== 'row-scope-suspect');
    if (unbound.length > 0) {
      logger.info(`  ${unbound.length} entit${unbound.length === 1 ? 'y' : 'ies'} could not be auto-bound — see references/global-search.md.`);
    }
  } catch (err) {
    logger.warning(`Global-search registration skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Back-fills the HR time-entry imputation seam by invoking the deterministic `scaffold-time-entry-refs`
 * discovery CLI. Idempotent and FAIL-SOFT — any problem here is logged and skipped, it never breaks the
 * upgrade. It NEVER auto-registers a dimension (imputation is an opt-in business decision): it only ensures
 * the COMMENTED seam wrapper exists (so an older project learns the seam) and reports the extension entities
 * that are candidate dimensions for a human to promote.
 */
async function syncTimeEntryRefsRegistration(
  projectDir: string,
  appCode: string,
  dryRun: boolean,
): Promise<void> {
  // Reuse the proven TEMPLATES_DIR base; the skills tree is a sibling of templates/project.
  const cli = join(dirname(TEMPLATES_DIR), 'skills', 'development', 'backend', 'data-layer', 'cli', 'scaffold-time-entry-refs', 'index.ts');
  if (!await fs.pathExists(cli)) {
    logger.debug('scaffold-time-entry-refs CLI not found, skipping time-entry-refs seam back-fill');
    return;
  }

  // Only relevant for projects that actually declare client extension entities.
  const ctxFiles = await glob('**/ExtensionsDbContext.cs', {
    cwd: join(projectDir, 'src'),
    ignore: ['**/bin/**', '**/obj/**'],
  });
  if (ctxFiles.length === 0) {
    logger.debug('No ExtensionsDbContext found, skipping time-entry-refs seam back-fill');
    return;
  }

  logger.info('Back-filling the HR time-entry imputation seam...');
  try {
    const flags = `--discover --project "${projectDir}" --app "${appCode}"${dryRun ? ' --dry-run' : ''}`;
    const out = execSync(`npx --prefer-offline tsx "${cli}" ${flags}`, {
      cwd: dirname(dirname(TEMPLATES_DIR)),
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    const env = JSON.parse(out) as {
      data?: {
        seamPresent?: boolean;
        seamBackfilled?: boolean;
        candidates?: string[];
        review?: Array<{ kind: string; entity?: string; detail: string }>;
      };
    };
    const candidates = env.data?.candidates ?? [];

    if (env.data?.seamBackfilled) {
      logger.success(`${dryRun ? '[DRY RUN] Would back-fill' : 'Back-filled'} the commented time-entry-refs seam (nothing registered — imputation is opt-in)`);
    } else if (env.data?.seamPresent) {
      logger.info(`Time-entry-refs seam ${chalk.green('✓')} (already present)`);
    } else {
      logger.info(`Time-entry-refs seam ${chalk.green('✓')} (nothing to back-fill)`);
    }
    if (candidates.length > 0) {
      logger.info(`  ${candidates.length} extension entit${candidates.length === 1 ? 'y is a' : 'ies are'} candidate imputation dimension${candidates.length === 1 ? '' : 's'} — register the ones time is booked against by hand. See references/time-entry-refs.md.`);
    }
  } catch (err) {
    logger.warning(`Time-entry-refs seam back-fill skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Back-fills the export-datasets seam into an existing project's DependencyInjection.cs.
 *
 * Unlike global search and time-entry refs, this seam has NO scaffolder — a dataset's fields, its
 * measures and its pivots are business decisions a column-mapping DSL cannot express — so there is
 * nothing to discover and nothing to auto-register. All this does is ensure the COMMENTED marker
 * block exists, so a project generated before the seam learns it is there.
 *
 * Idempotent and FAIL-SOFT: any problem is logged and skipped, it never breaks the upgrade.
 *
 * It also surfaces the v3.66 BEHAVIOURAL change for projects that already ship a provider — an
 * emptied filter used to fall back to the provider's defaults and now means "nothing selected". No
 * `ss upgrade` can rewrite a provider's business logic, so the only honest thing is to say so.
 */
async function syncExportDatasetsRegistration(
  projectDir: string,
  baseNamespace: string,
  dryRun: boolean,
): Promise<void> {
  const diPath = join(projectDir, 'src', `${baseNamespace}.Infrastructure`, 'DependencyInjection.cs');
  if (!await fs.pathExists(diPath)) {
    logger.debug('DependencyInjection.cs not found, skipping export-datasets seam back-fill');
    return;
  }

  try {
    const content = await fs.readFile(diPath, 'utf-8');
    const patched = insertExportDatasetsSeam(content, baseNamespace);

    if (content.includes(EXPORT_DATASETS_BEGIN)) {
      logger.info(`Export-datasets seam ${chalk.green('✓')} (already present)`);
    } else if (patched === null) {
      logger.info(
        `Export-datasets seam not back-filled — add the ${chalk.cyan('<<< EXPORT-DATASETS-DI >>>')} ` +
        'markers by hand. See references/export-datasets.md.',
      );
    } else {
      if (!dryRun) await fs.writeFile(diPath, patched);
      logger.success(
        `${dryRun ? '[DRY RUN] Would back-fill' : 'Back-filled'} the commented export-datasets seam ` +
        '(nothing registered — an exportable dataset is an opt-in business decision)',
      );
    }

    // A project that already registers a provider has business logic this upgrade cannot fix.
    if (content.includes('AddSmartStackExportDatasetProvider')) {
      logger.warning(
        '[REVIEW] Export datasets: an emptied filter now returns an EMPTY collection from ' +
        'ctx.Filter(...) instead of falling back to your DefaultValues. Return ' +
        'ExportDatasetResult.Empty on it, or unticking every option in the wizard will export ' +
        'everything. Stored templates are migrated on read. See references/export-datasets.md.',
      );
    }
  } catch (err) {
    logger.warning(`Export-datasets seam back-fill skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

async function syncFrontendPostinstallScript(
  frontendDir: string,
  dryRun: boolean,
): Promise<number> {
  const scriptDest = join(frontendDir, 'scripts', 'patch-smartstack-theme.cjs');
  const scriptTemplate = join(TEMPLATES_DIR, 'patch-smartstack-theme.cjs.template');

  if (!await fs.pathExists(scriptTemplate)) {
    logger.debug('patch-smartstack-theme.cjs.template not found, skipping');
    return 0;
  }

  let written = 0;
  if (!await fs.pathExists(scriptDest)) {
    if (dryRun) {
      logger.warning('[DRY RUN] Would create scripts/patch-smartstack-theme.cjs');
    } else {
      await fs.ensureDir(join(frontendDir, 'scripts'));
      const content = await fs.readFile(scriptTemplate, 'utf-8');
      await fs.writeFile(scriptDest, content, 'utf-8');
      logger.info(`  ${chalk.green('+')} scripts/patch-smartstack-theme.cjs`);
      written = 1;
    }
  }

  // Apply the patch immediately if node_modules is already installed.
  const themePath = join(
    frontendDir,
    'node_modules',
    '@atlashub',
    'smartstack',
    'dist',
    'theme.css',
  );
  if (!dryRun && await fs.pathExists(themePath)) {
    const original = await fs.readFile(themePath, 'utf-8');
    const patched = original.replace(/\*\*\/\*\.js/g, '** /*.js');
    if (patched !== original) {
      await fs.writeFile(themePath, patched, 'utf-8');
      logger.info(`  ${chalk.green('✓')} Patched node_modules/@atlashub/smartstack/dist/theme.css`);
    }
  }

  return written;
}

async function syncTestFrontendTemplates(
  frontendDir: string,
  dryRun: boolean,
): Promise<number> {
  const testFrontendDir = join(TEMPLATES_DIR, 'test-frontend');
  if (!await fs.pathExists(testFrontendDir)) {
    logger.debug('test-frontend templates not found, skipping');
    return 0;
  }

  let synced = 0;

  const testFiles = [
    { src: 'vitest.config.ts', dest: 'vitest.config.ts' },
    { src: 'setup.ts', dest: join('src', 'test', 'setup.ts') },
    { src: 'test-utils.tsx', dest: join('src', 'test', 'test-utils.tsx') },
    { src: 'msw/handlers.ts', dest: join('src', 'test', 'msw', 'handlers.ts') },
    { src: 'msw/server.ts', dest: join('src', 'test', 'msw', 'server.ts') },
  ];

  for (const file of testFiles) {
    const destPath = join(frontendDir, file.dest);
    if (await fs.pathExists(destPath)) continue;

    const srcPath = join(testFrontendDir, file.src);
    if (!await fs.pathExists(srcPath)) continue;

    if (dryRun) {
      logger.warning(`[DRY RUN] Would create ${file.dest}`);
    } else {
      await fs.ensureDir(dirname(destPath));
      const content = await fs.readFile(srcPath, 'utf-8');
      await fs.writeFile(destPath, content, 'utf-8');
      logger.info(`  ${chalk.green('+')} ${file.dest}`);
    }
    synced++;
  }

  return synced;
}

/** Deep-merge two parsed JSON trees, the EXISTING (target) side winning on
 *  every leaf conflict — hand-edited copy in the real locales tree is never
 *  overwritten by a recovered phantom bundle. */
function mergePreferExisting(incoming: unknown, existing: unknown): unknown {
  if (
    incoming !== null && existing !== null &&
    typeof incoming === 'object' && typeof existing === 'object' &&
    !Array.isArray(incoming) && !Array.isArray(existing)
  ) {
    const inc = incoming as Record<string, unknown>;
    const out: Record<string, unknown> = { ...inc };
    for (const [key, value] of Object.entries(existing as Record<string, unknown>)) {
      out[key] = key in inc ? mergePreferExisting(inc[key], value) : value;
    }
    return out;
  }
  return existing;
}

async function removeDirIfEmpty(dir: string): Promise<void> {
  try {
    const entries = await fs.readdir(dir);
    if (entries.length === 0) await fs.remove(dir);
  } catch {
    // missing or unreadable — leave it alone
  }
}

/**
 * Repairs the pre-5.11 scaffold-component bug that wrote module locale JSON to
 * `<frontendDir>/web/<app>-web/src/i18n/locales/…` — a phantom double-nested
 * tree the runtime never loads (frontendDir already IS the web root), so
 * business pages rendered raw i18n keys. Deep-merges every phantom bundle into
 * the real `src/i18n/locales/<locale>/` (existing target wins on leaf
 * conflicts) and prunes the phantom tree. Fail-soft: any error logs a warning
 * and returns what was already done.
 */
async function relocatePhantomI18nLocales(
  frontendDir: string,
  dryRun: boolean,
): Promise<number> {
  let relocated = 0;
  try {
    const phantomRoots = await glob('web/*-web/src/i18n/locales', { cwd: frontendDir, absolute: true });
    for (const phantomRoot of phantomRoots) {
      const locales = (await fs.readdir(phantomRoot, { withFileTypes: true }))
        .filter((d) => d.isDirectory())
        .map((d) => d.name);
      for (const locale of locales) {
        const localeDir = join(phantomRoot, locale);
        const bundles = (await fs.readdir(localeDir)).filter((f) => f.endsWith('.json'));
        for (const bundle of bundles) {
          const relDest = join('src', 'i18n', 'locales', locale, bundle);
          if (dryRun) {
            logger.warning(`[DRY RUN] Would recover phantom i18n bundle → ${relDest}`);
            relocated++;
            continue;
          }
          const src = join(localeDir, bundle);
          const dest = join(frontendDir, relDest);
          if (await fs.pathExists(dest)) {
            try {
              const incoming = JSON.parse(await fs.readFile(src, 'utf-8')) as unknown;
              const existing = JSON.parse(await fs.readFile(dest, 'utf-8')) as unknown;
              const merged = mergePreferExisting(incoming, existing);
              await fs.writeFile(dest, JSON.stringify(merged, null, 2) + '\n', 'utf-8');
            } catch {
              // malformed JSON on either side — keep the existing target untouched
              continue;
            }
          } else {
            await fs.ensureDir(dirname(dest));
            await fs.copy(src, dest);
          }
          logger.info(`  ${chalk.green('↻')} ${relDest} (recovered from phantom web/ tree)`);
          relocated++;
        }
      }
      if (!dryRun) {
        await fs.remove(phantomRoot); // …/web/<x>-web/src/i18n/locales
        const i18nDir = dirname(phantomRoot);
        await removeDirIfEmpty(i18nDir); // …/src/i18n
        await removeDirIfEmpty(dirname(i18nDir)); // …/src
        await removeDirIfEmpty(dirname(dirname(i18nDir))); // …/web/<x>-web
        await removeDirIfEmpty(join(frontendDir, 'web')); // …/web
      }
    }
  } catch (error) {
    logger.warning(`Phantom i18n relocation skipped: ${error instanceof Error ? error.message : String(error)}`);
  }
  return relocated;
}

/**
 * Removes the platform-admin fallback block from `componentRegistry.generated.ts`, then deletes
 * the placeholder component it leaves orphaned.
 *
 * `ss upgrade` never re-runs the frontend scaffolders (see the [ACTION REQUIRED] notice below),
 * so a project generated by CLI ≤ 5.12 would keep BLANK `/administration/*` landing pages until
 * someone happened to run /scaffold-routes. This is text surgery on a file the generator fully
 * owns — anchored on the emitted header, no brace scan, idempotent.
 *
 * Order matters: strip FIRST, classify second. Deleting the component while the registry still
 * imports it would turn a working page into a Vite resolution failure.
 *
 * Fail-soft, like its siblings: any error logs a warning and returns what was already done.
 */
async function syncPlatformAdminFallbacks(
  frontendDir: string,
  dryRun: boolean,
): Promise<number> {
  let changed = 0;
  try {
    const registryPath = join(frontendDir, 'src', 'extensions', 'componentRegistry.generated.ts');
    if (await fs.pathExists(registryPath)) {
      const content = await fs.readFile(registryPath, 'utf-8');
      const stripped = stripPlatformAdminFallbacks(content);
      if (stripped !== null) {
        if (!dryRun) await fs.writeFile(registryPath, stripped, 'utf-8');
        logger.success(
          `${dryRun ? '[DRY RUN] Would remove' : 'Removed'} the platform-admin fallback block from ` +
          `componentRegistry.generated.ts — /administration and every /administration/<module> root ` +
          `now render the SDK's own landing page instead of a blank placeholder`
        );
        changed++;
      }
    }

    const componentPath = join(frontendDir, 'src', 'components', 'AdminModuleFallback.tsx');
    if (await fs.pathExists(componentPath)) {
      const source = await fs.readFile(componentPath, 'utf-8');
      const others = await glob('src/**/*.{ts,tsx}', {
        cwd: frontendDir, absolute: true, ignore: MIGRATION_IGNORE_GLOBS,
      });
      const otherSources = await Promise.all(
        others.filter((f) => f !== componentPath).map((f) => fs.readFile(f, 'utf-8')),
      );
      switch (classifyAdminModuleFallback(source, otherSources)) {
        case 'orphan':
          if (!dryRun) await fs.remove(componentPath);
          logger.info(`  ${chalk.red('−')} ${dryRun ? '[DRY RUN] ' : ''}Removed orphan src/components/AdminModuleFallback.tsx`);
          changed++;
          break;
        case 'customised':
          logger.warning(
            'src/components/AdminModuleFallback.tsx is marked @customised and was KEPT, but nothing ' +
            `imports it anymore (${ADMIN_FALLBACK_SPECIFIER}). Delete it, or register it on a ` +
            'componentKey of your own.'
          );
          break;
        case 'still-referenced':
          logger.debug('AdminModuleFallback.tsx still referenced — kept.');
          break;
      }
    }
  } catch (error) {
    logger.warning(`Platform-admin fallback cleanup skipped: ${error instanceof Error ? error.message : String(error)}`);
  }
  return changed;
}

async function cleanResidualFiles(
  projectDir: string,
  baseNamespace: string,
  dryRun: boolean,
): Promise<number> {
  let cleaned = 0;

  const residuals = [
    join(projectDir, 'src', `${baseNamespace}.Api`, `${baseNamespace}.Api.http`),
  ];

  for (const file of residuals) {
    if (await fs.pathExists(file)) {
      if (dryRun) {
        logger.warning(`[DRY RUN] Would remove residual: ${file}`);
      } else {
        await fs.remove(file);
        logger.info(`  ${chalk.red('−')} Removed residual: ${baseNamespace}.Api.http`);
      }
      cleaned++;
    }
  }

  return cleaned;
}

async function fixInitStateVersion(
  projectDir: string,
  cliVersion: string,
  dryRun: boolean,
): Promise<boolean> {
  const statePath = join(projectDir, '.smartstack', 'init-state.json');
  if (!await fs.pathExists(statePath)) return false;

  try {
    const state = await fs.readJson(statePath);
    if (state.cliVersion === '0.0.0') {
      if (dryRun) {
        logger.warning('[DRY RUN] Would fix cliVersion 0.0.0 in init-state.json');
      } else {
        state.cliVersion = cliVersion;
        await fs.writeJson(statePath, state, { spaces: 2 });
        logger.info(`  ${chalk.green('+')} Fixed cliVersion: 0.0.0 → ${cliVersion}`);
      }
      return true;
    }
  } catch {
    // Non-critical
  }
  return false;
}

/**
 * Ensures all required NuGet packages are present in each project.
 * Adds missing packages without removing existing ones.
 * Handles projects created by older CLI versions that may be missing
 * packages added in later releases (e.g., SignalR.StackExchangeRedis).
 */
async function ensureRequiredPackages(
  projects: ProjectUpgradeInfo[],
  baseNamespace: string,
  dryRun: boolean,
): Promise<{ added: string[]; failed: string[] }> {
  const added: string[] = [];
  const failed: string[] = [];

  for (const rp of REQUIRED_PACKAGES) {
    if (rp.packages.length === 0) continue;

    // Find the project matching this suffix (e.g., "MyApp.Api")
    const expectedFolder = `${baseNamespace}.${rp.suffix}`;
    const project = projects.find(p => {
      const folderName = p.relPath.split(/[/\\]/).slice(-2, -1)[0];
      return folderName === expectedFolder;
    });

    if (!project) continue;

    const currentPackages = readAllPackageReferences(project.csprojContent);
    const currentNames = new Set(currentPackages.map(p => p.name.toLowerCase()));

    for (const requiredPkg of rp.packages) {
      if (currentNames.has(requiredPkg.toLowerCase())) continue;

      // Package is missing — add it. For locked families (e.g. Hangfire.*) pin the
      // version to the family member already in the .csproj; otherwise `dotnet add`
      // resolves the floating latest whose exact core dependency outranks the existing
      // direct reference → NU1605 (package downgrade) → broken restore.
      const pin = resolveLockedFamilyVersion(requiredPkg, currentPackages);
      const versionFlag = pin ? ` --version ${pin}` : '';
      const pinNote = pin
        ? ` ${chalk.gray(`(pinned to ${pin} to match existing ${requiredPkg.split('.')[0]}.* reference)`)}`
        : '';
      try {
        if (dryRun) {
          logger.info(`  [DRY RUN] Would add ${chalk.cyan(requiredPkg)}${pin ? `@${pin}` : ''} to ${chalk.cyan(expectedFolder)}`);
        } else {
          execCommand(
            `dotnet add "${project.csprojPath}" package ${requiredPkg}${versionFlag} --no-restore`,
            undefined,
            false,
          );
          logger.success(`  ${chalk.green('+')} Added ${chalk.cyan(requiredPkg)} to ${chalk.cyan(expectedFolder)}${pinNote}`);
        }
        added.push(`${expectedFolder} + ${requiredPkg}`);
      } catch {
        failed.push(`${expectedFolder} + ${requiredPkg}`);
        logger.error(`  ${chalk.red('✗')} Failed to add ${requiredPkg} to ${expectedFolder}`);
      }
    }
  }

  return { added, failed };
}

export const upgradeCommand = new Command('upgrade')
  .description('Upgrade SmartStack packages to the latest version')
  .option('--preview', 'Upgrade to latest preview/prerelease version')
  .option('--dry-run', 'Show what would be upgraded without actually upgrading')
  .option('--all-packages', 'Also upgrade all other NuGet packages (slow, disabled by default)')
  .option('--force-docker', 'Overwrite locally modified docker files (a .bak backup of each is kept)')
  .action(async (options: UpgradeOptions) => {
    logger.header('SmartStack Package Upgrade');

    const dryRun = options.dryRun || false;
    const result: UpgradeResult = {
      nugetUpgraded: 0,
      nugetSkipped: 0,
      nugetFailed: 0,
      otherPkgUpdated: 0,
      otherPkgSkipped: 0,
      otherPkgFailed: 0,
      npmUpgraded: false,
      npmSkipped: false,
      npmOtherUpdated: false,
      configSynced: 0,
      claudeSettingsSynced: 0,
      ralphPurged: false,
      dockerSynced: 0,
      dockerSkippedModified: [],
      testFrontendSynced: 0,
      i18nBundlesRelocated: 0,
      adminFallbacksCleaned: 0,
      residualsCleaned: 0,
      initStateFixed: false,
      requiredPkgAdded: 0,
      requiredPkgFailed: 0,
      restoreFailed: false,
      packagesAligned: 0,
      unresolvedDowngrades: [],
      programCsIssues: [],
    };

    if (dryRun) {
      logger.warning('DRY RUN MODE - No packages will be upgraded');
      console.log();
    }

    // Detect SmartStack project
    const detection = await detectSmartStackProject();

    if (!detection.isProject) {
      logger.error('Not a SmartStack project. Run this command from a SmartStack project root.');
      logger.info('Tip: SmartStack projects contain a .smartstack/config.json file.');
      process.exitCode = 1;
      return;
    }

    const { config, projectDir } = detection;
    logger.info(`Project: ${chalk.cyan(config!.baseNamespace)}`);
    logger.info(`Type: ${chalk.cyan(config!.projectType)}`);

    // Get current SmartStack version from config
    const configVersion = config!.smartStackVersion || 'unknown';
    logger.info(`Config version: ${chalk.yellow(configVersion)}`);
    console.log();

    // Determine version type
    const usePreview = options.preview || false;
    logger.info(`Target: ${usePreview ? chalk.yellow('Preview/Prerelease') : chalk.green('Stable')}`);
    console.log();

    // Fetch latest versions
    logger.info('Fetching latest versions...');
    const nugetVersion = await getLatestNuGetVersion('SmartStack', usePreview);
    const npmVersion = await getLatestNpmVersion('@atlashub/smartstack', usePreview);

    if (!nugetVersion) {
      logger.error('Could not fetch latest NuGet version. Check your internet connection.');
      process.exitCode = 1;
      return;
    }

    logger.success(`Latest NuGet version: ${chalk.cyan(nugetVersion)}`);
    if (npmVersion) {
      logger.success(`Latest npm version: ${chalk.cyan(npmVersion)}`);
    }
    console.log();

    // Find all .NET projects in src/
    logger.info('Scanning for .NET projects...');
    const allProjects = await findAllProjects(projectDir);

    // Restore once and, when NuGet reports a downgrade (NU1605), auto-align the offending
    // direct references to the new transitive floor and re-restore. Mutates `result`.
    const restoreAndRepair = async (): Promise<void> => {
      const restore = execCapture('dotnet restore', projectDir);
      if (restore.ok) {
        result.restoreFailed = false; // last restore is authoritative
        return;
      }
      const repair = await repairNuGetDowngrades(restore.output, allProjects, projectDir, dryRun);
      result.packagesAligned += repair.aligned.length;
      if (repair.resolved) {
        result.restoreFailed = false; // last restore is authoritative
        logger.success(`Restore recovered — aligned ${chalk.cyan(repair.aligned.length)} package(s) to SmartStack's dependency floor`);
        return;
      }
      result.restoreFailed = true;
      if (repair.unresolved.length > 0) {
        result.unresolvedDowngrades = [...new Set([...result.unresolvedDowngrades, ...repair.unresolved])];
      }
      logger.error('dotnet restore failed — see the NuGet error(s) above');
    };

    if (allProjects.length === 0) {
      logger.warning('No .NET projects found in src/ directory.');
    } else {
      // Separate projects with and without SmartStack
      const smartStackProjects = allProjects.filter(p => p.currentVersion !== null);
      const otherProjects = allProjects.filter(p => p.currentVersion === null);

      // Display SmartStack projects
      if (smartStackProjects.length > 0) {
        logger.info(`Found ${chalk.cyan(smartStackProjects.length)} project(s) with SmartStack:`);
        for (const p of smartStackProjects) {
          const versionInfo = `${chalk.yellow(p.currentVersion)} → ${chalk.cyan(nugetVersion)}`;
          const upToDate = p.currentVersion === nugetVersion;
          const statusIcon = upToDate ? chalk.green('✓') : chalk.yellow('↑');
          logger.info(`  ${statusIcon} ${p.relPath} (${upToDate ? chalk.green('up to date') : versionInfo})`);
        }
      }

      // Display other projects
      if (otherProjects.length > 0) {
        logger.info(`Found ${chalk.cyan(otherProjects.length)} other project(s):`);
        for (const p of otherProjects) {
          logger.info(`  ${chalk.gray('○')} ${p.relPath}`);
        }
      }
      console.log();

      // Upgrade SmartStack in projects that have it
      if (smartStackProjects.length > 0) {
        const projectsToUpgrade = smartStackProjects.filter(p => p.currentVersion !== nugetVersion);
        result.nugetSkipped = smartStackProjects.length - projectsToUpgrade.length;

        if (projectsToUpgrade.length === 0) {
          logger.success(`All ${smartStackProjects.length} SmartStack project(s) already at ${chalk.cyan(nugetVersion)}`);
        } else {
          // Add to every project with --no-restore, then restore ONCE so a version
          // conflict surfaces in captured output and can be auto-repaired (NU1605).
          logger.info(`Upgrading SmartStack in ${projectsToUpgrade.length} project(s)...`);
          for (const project of projectsToUpgrade) {
            logger.info(`Upgrading ${chalk.cyan(project.relPath)}...`);
            try {
              execCommand(`dotnet add "${project.csprojPath}" package SmartStack --version ${nugetVersion} --no-restore`, undefined, dryRun);
              result.nugetUpgraded++;
            } catch (error) {
              result.nugetFailed++;
              logger.error(`Failed to upgrade ${project.relPath}: ${error instanceof Error ? error.message : error}`);
            }
          }

          // Single restore for all projects (with NU1605 downgrade auto-repair)
          if (result.nugetUpgraded > 0 && !dryRun) {
            logger.info('Restoring NuGet packages...');
            await restoreAndRepair();
          }

          if (result.nugetUpgraded > 0) {
            logger.success(`${result.nugetUpgraded} project(s) upgraded to ${chalk.cyan(nugetVersion)}`);
          }
          if (result.nugetFailed > 0) {
            logger.error(`${result.nugetFailed} project(s) failed to upgrade`);
          }
        }
        console.log();
      }

      // Ensure required packages are present (reconcile older projects)
      logger.info('Checking required NuGet packages...');
      // Re-read csproj content if SmartStack upgrade modified them
      if (result.nugetUpgraded > 0) {
        for (const project of allProjects) {
          if (project.currentVersion !== null) {
            project.csprojContent = await fs.readFile(project.csprojPath, 'utf-8');
          }
        }
      }
      const requiredResult = await ensureRequiredPackages(
        allProjects,
        config!.baseNamespace,
        dryRun,
      );
      result.requiredPkgAdded = requiredResult.added.length;
      result.requiredPkgFailed = requiredResult.failed.length;

      if (requiredResult.added.length > 0) {
        logger.success(`${requiredResult.added.length} required package(s) added`);
        if (!dryRun) {
          logger.info('Restoring NuGet packages...');
          await restoreAndRepair();
        }
      } else {
        logger.info(`Required packages ${chalk.green('✓')} all present`);
      }
      if (requiredResult.failed.length > 0) {
        logger.error(`${requiredResult.failed.length} required package(s) failed to add`);
      }
      console.log();

      // Upgrade other NuGet packages only if --all-packages is specified
      if (options.allPackages) {
        logger.info('Checking other NuGet packages...');
        for (const project of allProjects) {
          // Re-read csproj if SmartStack upgrade or required packages modified it
          const content = (result.nugetUpgraded > 0 && project.currentVersion !== null) || result.requiredPkgAdded > 0
            ? await fs.readFile(project.csprojPath, 'utf-8')
            : project.csprojContent;
          const allPackages = readAllPackageReferences(content);
          const otherPackages = allPackages.filter(p => p.name !== 'SmartStack');

          if (otherPackages.length === 0) continue;

          logger.info(`  ${chalk.cyan(project.relPath)}:`);
          for (const pkg of otherPackages) {
            try {
              // Use --no-restore to skip implicit restore per package
              if (dryRun) {
                logger.info(`    [DRY RUN] ${pkg.name} ${chalk.yellow(pkg.currentVersion)} → latest`);
                result.otherPkgUpdated++;
                continue;
              }

              execSync(
                `dotnet add "${project.csprojPath}" package ${pkg.name} --no-restore`,
                { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }
              );

              // Re-read the .csproj to check if the version actually changed
              const newContent = await fs.readFile(project.csprojPath, 'utf-8');
              const newVersion = readPackageVersion(newContent, pkg.name);

              if (newVersion && newVersion !== pkg.currentVersion) {
                logger.success(`    ${chalk.green('✓')} ${pkg.name} ${chalk.yellow(pkg.currentVersion)} → ${chalk.cyan(newVersion)}`);
                result.otherPkgUpdated++;
              } else {
                result.otherPkgSkipped++;
              }
            } catch (error) {
              result.otherPkgFailed++;
              logger.error(`    ${chalk.red('✗')} ${pkg.name}: ${error instanceof Error ? error.message : error}`);
            }
          }

          // Single restore per project after all packages updated
          if (!dryRun && (result.otherPkgUpdated > 0 || result.otherPkgFailed > 0)) {
            logger.info(`  Restoring ${chalk.cyan(project.relPath)}...`);
            tryExecCommand(`dotnet restore "${project.csprojPath}"`, undefined);
          }
        }

        if (result.otherPkgUpdated > 0) {
          logger.success(`${result.otherPkgUpdated} package(s) updated`);
        }
        if (result.otherPkgSkipped > 0) {
          logger.info(`${result.otherPkgSkipped} package(s) already at latest`);
        }
        if (result.otherPkgFailed > 0) {
          logger.error(`${result.otherPkgFailed} package(s) failed to update`);
        }
        console.log();
      } else {
        logger.info(`Other NuGet packages: ${chalk.gray('skipped')} (use ${chalk.cyan('--all-packages')} to upgrade all)`);
        console.log();
      }
    }

    // Find and upgrade frontend
    const frontendDir = await findFrontendDirectory(projectDir);

    if (!frontendDir) {
      logger.warning('No frontend directory found with @atlashub/smartstack package.');
    } else {
      const relFrontendPath = frontendDir.replace(projectDir, '').replace(/^[/\\]/, '');
      const currentFrontendVersion = await readFrontendVersion(frontendDir);

      if (currentFrontendVersion && npmVersion && currentFrontendVersion === npmVersion) {
        logger.info(`Frontend: ${chalk.cyan(relFrontendPath)} ${chalk.green('✓')} already at ${chalk.cyan(npmVersion)}`);
        result.npmSkipped = true;
      } else if (npmVersion) {
        const versionInfo = currentFrontendVersion
          ? `${chalk.yellow(currentFrontendVersion)} → ${chalk.cyan(npmVersion)}`
          : `→ ${chalk.cyan(npmVersion)}`;
        logger.info(`Frontend: ${chalk.cyan(relFrontendPath)} (${versionInfo})`);
        logger.info('Upgrading npm package...');

        // Try specific version first
        const success = tryExecCommand(`npm install @atlashub/smartstack@${npmVersion}`, frontendDir, dryRun);

        if (success) {
          logger.success(`npm package upgraded to ${chalk.cyan(npmVersion)}`);
          result.npmUpgraded = true;
        } else {
          // Fallback to @next tag if specific version fails (propagation issue)
          logger.warning(`Version ${npmVersion} not yet available, trying @next tag...`);
          const tag = usePreview ? 'next' : 'latest';
          const fallbackSuccess = tryExecCommand(`npm install @atlashub/smartstack@${tag}`, frontendDir, dryRun);

          if (fallbackSuccess) {
            logger.success(`npm package upgraded to @${tag}`);
            result.npmUpgraded = true;
          } else {
            logger.error('Failed to upgrade npm package. Please try again later or run: npm install @atlashub/smartstack@next');
          }
        }
      } else {
        logger.warning('Could not fetch npm version, skipping frontend upgrade.');
      }

      // Upgrade all other npm packages if --all-packages is specified
      if (options.allPackages) {
        logger.info('Updating other npm packages...');
        const npmUpdateSuccess = tryExecCommand('npm update', frontendDir, dryRun);
        if (npmUpdateSuccess) {
          logger.success('npm packages updated');
          result.npmOtherUpdated = true;
        } else {
          logger.error('Failed to update npm packages');
        }
      } else {
        logger.info(`Other npm packages: ${chalk.gray('skipped')} (use ${chalk.cyan('--all-packages')} to upgrade all)`);
      }
      // Sync test-frontend templates (vitest, MSW) if missing
      logger.info('Syncing test-frontend templates...');
      const testSynced = await syncTestFrontendTemplates(frontendDir, dryRun);
      result.testFrontendSynced = testSynced;

      if (testSynced > 0) {
        logger.success(`${testSynced} test template(s) deployed`);
      } else {
        logger.info(`Test templates ${chalk.green('✓')} up to date`);
      }

      // Sync postinstall script (Tailwind v4 / theme.css workaround)
      logger.info('Syncing postinstall theme patcher...');
      await syncFrontendPostinstallScript(frontendDir, dryRun);

      // Recover locale bundles from the phantom web/<app>-web tree (pre-5.11
      // scaffold-component wrote them there — the runtime never loads them).
      result.i18nBundlesRelocated = await relocatePhantomI18nLocales(frontendDir, dryRun);
      if (result.i18nBundlesRelocated > 0) {
        logger.success(
          `${result.i18nBundlesRelocated} i18n bundle(s) recovered from the phantom web/ tree — ` +
          `run /scaffold-routes (aggregate) in Claude Code to re-register them`
        );
      }

      result.adminFallbacksCleaned = await syncPlatformAdminFallbacks(frontendDir, dryRun);

      console.log();
    }

    // Execute code migrations
    logger.info('Checking for code migrations...');
    const migrationSummary = await executeMigrations(
      projectDir,
      configVersion,
      nugetVersion,
      dryRun
    );

    if (migrationSummary.totalApplied > 0 || migrationSummary.totalSkipped > 0) {
      logger.info(`Code migrations: ${chalk.green(migrationSummary.totalApplied)} applied, ${chalk.gray(migrationSummary.totalSkipped)} skipped`);

      if (migrationSummary.hasErrors) {
        logger.warning('Some migrations failed. Please review the errors above.');
      }

      console.log();
    } else {
      logger.info('No code migrations needed.');
      console.log();
    }

    // Register client extension entities for the platform global search (idempotent, fail-soft).
    await syncExtensionSearchRegistration(projectDir, config!.baseNamespace, dryRun);
    console.log();

    // Back-fill the HR time-entry imputation seam (idempotent, fail-soft — never auto-registers).
    await syncTimeEntryRefsRegistration(projectDir, config!.baseNamespace, dryRun);
    console.log();

    // Back-fill the export-datasets seam (idempotent, fail-soft — no scaffolder, nothing to register).
    await syncExportDatasetsRegistration(projectDir, config!.baseNamespace, dryRun);
    console.log();

    // Remind user to regenerate componentRegistry to pick up new built-in pages from upgraded package
    if (frontendDir && npmVersion) {
      logger.warning(
        `[ACTION REQUIRED] Run ${chalk.cyan('/scaffold-routes')} in Claude Code to regenerate ` +
        `componentRegistry.generated.ts with all built-in pages from @atlashub/smartstack@${npmVersion}. ` +
        `Skipping this step may cause MISSING_PAGE errors at runtime (e.g. MISSING_PAGE/<section key>).`
      );
      console.log();
      // ss upgrade NEVER re-runs the frontend scaffolders itself: a component
      // re-scaffold on this CLI version imports @/components/ui/SectionCard,
      // which only scaffold-ui-primitives emits — without this step every
      // regenerated page fails validate-page's imports-resolve gate.
      logger.warning(
        `[ACTION REQUIRED] Run ${chalk.cyan('scaffold-ui-primitives')} in Claude Code before any page ` +
        `re-scaffold: this CLI version's pages import the new SectionCard primitive ` +
        `(src/components/ui/SectionCard.tsx), emitted only by scaffold-ui-primitives.`
      );
      console.log();
    }

    // Reconcile appsettings.json with the template (add new keys, prune deprecated)
    logger.info('Syncing appsettings.json...');
    const { added: addedKeys, removed: removedKeys, relocated: relocatedKeys } = await syncAppSettings(projectDir, config!.baseNamespace, dryRun);
    result.configSynced = addedKeys.length + removedKeys.length + relocatedKeys.length;

    if (addedKeys.length > 0 || removedKeys.length > 0 || relocatedKeys.length > 0) {
      const relocatedSuffix = relocatedKeys.length > 0 ? ` / ↻${relocatedKeys.length}` : '';
      if (dryRun) {
        logger.warning(`[DRY RUN] Would reconcile appsettings.json (+${addedKeys.length} / -${removedKeys.length}${relocatedSuffix})`);
      }
      for (const key of relocatedKeys) {
        logger.info(`  ${chalk.cyan('↻')} ${key} (relocated to its new section)`);
      }
      for (const key of addedKeys) {
        logger.info(`  ${chalk.green('+')} ${key}`);
      }
      for (const key of removedKeys) {
        logger.info(`  ${chalk.red('-')} ${key}`);
      }
      logger.success(`appsettings.json reconciled (+${addedKeys.length} / -${removedKeys.length}${relocatedSuffix})`);
      console.log();
    } else {
      logger.info(`appsettings.json ${chalk.green('✓')} up to date`);
      console.log();
    }

    // Sync .claude/settings.json (add missing keys, never delete)
    logger.info('Syncing .claude/settings.json...');
    const addedClaudeKeys = await syncClaudeSettings(projectDir, dryRun);
    result.claudeSettingsSynced = addedClaudeKeys.length;

    if (addedClaudeKeys.length > 0) {
      if (dryRun) {
        logger.warning(`[DRY RUN] Would add ${addedClaudeKeys.length} new setting(s) to .claude/settings.json`);
      }
      for (const key of addedClaudeKeys) {
        logger.info(`  ${chalk.green('+')} ${key}`);
      }
      logger.success(`${addedClaudeKeys.length} new setting(s) added to .claude/settings.json`);
      console.log();
    } else {
      logger.info(`.claude/settings.json ${chalk.green('✓')} up to date`);
      console.log();
    }

    // v5 migration: remove legacy MCP server entries (smartstack, context7)
    try {
      const mcpCleanup = await removeMcpServerRegistration();
      if (mcpCleanup.cleaned.length > 0) {
        for (const path of mcpCleanup.cleaned) {
          logger.success(`Removed legacy MCP entries from ${path}`);
        }
      } else if (mcpCleanup.errors.length === 0) {
        logger.info(`Legacy MCP entries ${chalk.green('✓')} not found`);
      }
      for (const err of mcpCleanup.errors) {
        logger.warning(`MCP cleanup: ${err}`);
      }
    } catch (error) {
      logger.warning(`MCP cleanup failed: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    // Configure Claude Code environment variables
    try {
      const envResult = await configureClaudeEnvironment();
      if (envResult.success) {
        logger.info(`Claude Code environment ${chalk.green('✓')} MAX_OUTPUT_TOKENS=64000`);
      } else {
        logger.warning(`Failed to configure Claude environment: ${envResult.error}`);
      }
    } catch {
      // Non-critical
    }
    console.log();

    // Sync docker files — never silently overwrite a client's local edits.
    // Baseline = init-state hashes, plus adoption of pristine files matching a
    // historical template rendition (legacy projects predate the tracking).
    logger.info('Syncing docker files...');
    let initState = await loadInitState(projectDir);
    if (!initState) {
      // Project predates state tracking entirely — synthetic state, persisted
      // only if the sync records at least one hash.
      initState = createCompletedInitState(config!, nugetVersion ?? '0.0.0');
    }
    const dockerSummary = await syncDockerFiles({
      projectDir,
      projectName: config!.baseNamespace,
      templatesDir: TEMPLATES_DIR,
      state: initState,
      dryRun,
      force: options.forceDocker ?? false,
    });
    for (const fileResult of dockerSummary.files) {
      logDockerFileResult(fileResult, dryRun);
    }
    result.dockerSynced = dockerSummary.written;
    result.dockerSkippedModified = dockerSummary.skippedModified;
    if (!dryRun && dockerSummary.stateChanged) {
      initState.updatedAt = new Date().toISOString();
      await saveInitState(projectDir, initState);
    }

    if (dockerSummary.written > 0) {
      logger.success(`${dockerSummary.written} docker file(s) updated`);
    } else if (dockerSummary.skippedModified.length === 0) {
      logger.info(`Docker files ${chalk.green('✓')} up to date`);
    }
    logSkippedModifiedSummary(dockerSummary.skippedModified);
    console.log();

    // Clean residual files left by dotnet new (e.g. .http files)
    const residualsCleaned = await cleanResidualFiles(projectDir, config!.baseNamespace, dryRun);
    result.residualsCleaned = residualsCleaned;
    if (residualsCleaned > 0) {
      logger.success(`${residualsCleaned} residual file(s) cleaned`);
      console.log();
    }

    // Fix init-state.json if cliVersion is 0.0.0 (pre-fix bug)
    result.initStateFixed = await fixInitStateVersion(projectDir, nugetVersion, dryRun);
    if (result.initStateFixed) {
      logger.success('Fixed cliVersion in init-state.json');
      console.log();
    }

    // Remove the legacy .ralph/ directory (Ralph was replaced by /ba-develop)
    if (dryRun) {
      if (await fs.pathExists(join(projectDir, '.ralph'))) {
        logger.info(`[DRY RUN] Would remove legacy ${chalk.cyan('.ralph/')} directory`);
      }
    } else {
      result.ralphPurged = await purgeRalphProjectArtifacts(projectDir);
    }
    console.log();

    // Validate Program.cs has all required SmartStack bootstrap calls
    logger.info('Validating Program.cs...');
    result.programCsIssues = await validateProgramCs(projectDir, config!.baseNamespace);

    if (result.programCsIssues.length > 0) {
      logger.error('Program.cs is missing required SmartStack calls:');
      for (const issue of result.programCsIssues) {
        logger.warning(`  ${chalk.red('✗')} ${issue}`);
      }
      console.log();
      logger.warning('Your Program.cs must contain these 4 calls in order:');
      logger.info(`  1. builder.Services.${chalk.cyan('AddSmartStack')}(builder.Configuration);`);
      logger.info(`  2. await app.${chalk.cyan('InitializeSmartStackAsync')}();`);
      logger.info(`  3. app.${chalk.cyan('UseSmartStack')}();`);
      logger.info(`  4. app.${chalk.cyan('MapSmartStack')}();`);
      console.log();
      logger.info(`Reference template: ${chalk.cyan('ss init --dry-run')} or check SmartStack docs`);
      console.log();
    } else {
      logger.info(`Program.cs ${chalk.green('✓')} all SmartStack calls present`);
      console.log();
    }

    // Update SmartStack version in config — but NOT over a broken restore. Advancing the
    // recorded version while the solution does not restore would make the next
    // `ss upgrade` believe the migration finished, leaving no marker that it didn't.
    // Leaving the old version is that marker.
    if (!dryRun && nugetVersion && !result.restoreFailed) {
      const configPath = join(projectDir, '.smartstack', 'config.json');
      if (await fs.pathExists(configPath)) {
        config!.smartStackVersion = nugetVersion;
        await fs.writeJson(configPath, config, { spaces: 2 });
        logger.success(`Updated config version to ${chalk.cyan(nugetVersion)}`);
      }
      console.log();
    } else if (!dryRun && nugetVersion && result.restoreFailed) {
      logger.warning(`Config version left at ${chalk.yellow(configVersion)} — restore is broken; fix it, then re-run ${chalk.cyan('ss upgrade')} to record ${chalk.cyan(nugetVersion)}`);
      console.log();
    }

    // Summary - reflect actual actions
    const totalChanged = result.nugetUpgraded + result.requiredPkgAdded + result.packagesAligned + result.otherPkgUpdated + (result.npmUpgraded ? 1 : 0) + (result.npmOtherUpdated ? 1 : 0) + migrationSummary.totalApplied + result.configSynced + result.claudeSettingsSynced + result.dockerSynced + result.testFrontendSynced + result.residualsCleaned + (result.initStateFixed ? 1 : 0) + (result.ralphPurged ? 1 : 0);
    const allUpToDate = totalChanged === 0 && result.nugetFailed === 0 && result.otherPkgFailed === 0
      && result.requiredPkgFailed === 0 && !result.restoreFailed;
    const hasProgramIssues = result.programCsIssues.length > 0;
    const hasDockerSkips = result.dockerSkippedModified.length > 0;

    if (allUpToDate && !hasProgramIssues && !hasDockerSkips) {
      const summary = [
        chalk.green.bold('Already up to date!'),
        '',
        `  NuGet SmartStack: ${chalk.cyan(nugetVersion)}`,
        npmVersion ? `  npm @atlashub/smartstack: ${chalk.cyan(npmVersion)}` : '',
        '',
        'All packages at latest versions.',
      ].filter(Boolean);

      logger.box(summary, 'success');
    } else if (allUpToDate && (hasProgramIssues || hasDockerSkips)) {
      const summary = [
        chalk.yellow.bold(hasProgramIssues
          ? 'Packages up to date, but Program.cs needs attention!'
          : 'Packages up to date, but docker files need attention!'),
        '',
      ];
      if (hasProgramIssues) {
        summary.push(
          `  ${chalk.red('⚠')} Missing: ${result.programCsIssues.join(', ')}`,
          '',
          `  Fix: Ensure Program.cs contains all 4 SmartStack bootstrap calls`,
          `  Ref: ${chalk.cyan('ss init --dry-run')} to see the correct template`,
        );
      }
      if (hasDockerSkips) {
        if (hasProgramIssues) summary.push('');
        summary.push(
          `  ${chalk.yellow('⚠')} Docker: ${result.dockerSkippedModified.length} locally modified file(s) NOT updated:`,
          ...result.dockerSkippedModified.map(f => `      - ${f}`),
          '',
          `  Overwrite with: ${chalk.cyan('ss upgrade --force-docker')} (a .bak backup of each file is kept)`,
        );
      }

      logger.box(summary, 'warning');
    } else {
      const outcome = computeUpgradeOutcome(result);
      const lines: string[] = [
        outcome.title,
        '',
      ];

      if (result.nugetUpgraded > 0) {
        lines.push(`  ${chalk.green('✓')} SmartStack: ${result.nugetUpgraded} project(s) → ${chalk.cyan(nugetVersion)}`);
      }
      if (result.nugetSkipped > 0) {
        lines.push(`  ${chalk.gray('–')} SmartStack: ${result.nugetSkipped} project(s) already up to date`);
      }
      if (result.nugetFailed > 0) {
        lines.push(`  ${chalk.red('✗')} SmartStack: ${result.nugetFailed} project(s) failed`);
      }
      if (result.requiredPkgAdded > 0) {
        lines.push(`  ${chalk.green('✓')} Required packages: ${result.requiredPkgAdded} added`);
      }
      if (result.packagesAligned > 0) {
        lines.push(`  ${chalk.green('✓')} Aligned ${result.packagesAligned} direct reference(s) to SmartStack's dependency floor (NU1605)`);
      }
      if (result.otherPkgUpdated > 0) {
        lines.push(`  ${chalk.green('✓')} Other NuGet: ${result.otherPkgUpdated} package(s) updated`);
      }
      if (result.otherPkgFailed > 0) {
        lines.push(`  ${chalk.red('✗')} Other NuGet: ${result.otherPkgFailed} package(s) failed`);
      }
      if (result.npmUpgraded) {
        lines.push(`  ${chalk.green('✓')} npm: @atlashub/smartstack → ${chalk.cyan(npmVersion)}`);
      }
      if (result.npmSkipped) {
        lines.push(`  ${chalk.gray('–')} npm: already up to date`);
      }
      if (result.npmOtherUpdated) {
        lines.push(`  ${chalk.green('✓')} npm: other packages updated`);
      }
      if (migrationSummary.totalApplied > 0) {
        lines.push(`  ${chalk.green('✓')} Code migrations: ${migrationSummary.totalApplied} applied`);
      }
      if (result.configSynced > 0) {
        lines.push(`  ${chalk.green('✓')} Config: ${result.configSynced} setting(s) reconciled`);
      }
      if (result.claudeSettingsSynced > 0) {
        lines.push(`  ${chalk.green('✓')} Claude: ${result.claudeSettingsSynced} new setting(s) added`);
      }
      if (result.dockerSynced > 0) {
        lines.push(`  ${chalk.green('✓')} Docker: ${result.dockerSynced} file(s) updated`);
      }
      if (result.dockerSkippedModified.length > 0) {
        lines.push(`  ${chalk.yellow('⚠')} Docker: ${result.dockerSkippedModified.length} file(s) skipped (locally modified — use ${chalk.cyan('--force-docker')})`);
      }
      if (result.testFrontendSynced > 0) {
        lines.push(`  ${chalk.green('✓')} Test templates: ${result.testFrontendSynced} file(s) deployed`);
      }
      if (result.adminFallbacksCleaned > 0) {
        lines.push(`  ${chalk.green('✓')} Admin pages: platform-admin fallback registrations removed`);
      }
      if (result.residualsCleaned > 0) {
        lines.push(`  ${chalk.green('✓')} Cleanup: ${result.residualsCleaned} residual file(s) removed`);
      }
      if (result.initStateFixed) {
        lines.push(`  ${chalk.green('✓')} Init-state: cliVersion fixed`);
      }
      if (result.ralphPurged) {
        lines.push(`  ${chalk.green('✓')} Legacy .ralph/ removed (replaced by /ba-develop)`);
      }
      if (result.requiredPkgFailed > 0) {
        lines.push(`  ${chalk.red('✗')} Required packages: ${result.requiredPkgFailed} failed to add`);
      }
      if (result.restoreFailed) {
        lines.push(`  ${chalk.red('✗')} NuGet restore failed — packages were edited but the solution does not restore`);
      }
      if (result.programCsIssues.length > 0) {
        lines.push(`  ${chalk.red('⚠')} Program.cs: ${result.programCsIssues.length} missing call(s) - manual fix required`);
      }

      lines.push('');
      lines.push(chalk.yellow('Next steps:'));
      if (result.restoreFailed) {
        const conflict = result.unresolvedDowngrades.length > 0
          ? `align the conflicting version(s): ${result.unresolvedDowngrades.join(', ')}`
          : 'align the conflicting versions';
        lines.push(`  ${chalk.red('!')} Fix the NuGet error above (${conflict}), then run ${chalk.cyan('dotnet restore')}`);
      }
      if (result.programCsIssues.length > 0) {
        lines.push(`  ${chalk.red('!')} Fix Program.cs (see warnings above)`);
      }
      if (frontendDir && npmVersion) {
        lines.push(`  ${chalk.yellow('!')} Run ${chalk.cyan('/scaffold-routes')} in Claude Code (regenerates componentRegistry — avoids MISSING_PAGE)`);
      }
      lines.push(`  1. Review changes: ${chalk.cyan('git diff')}`);
      lines.push(`  2. Test your application`);
      lines.push(`  3. Commit changes: ${chalk.cyan('git add . && git commit -m "chore: upgrade SmartStack to ' + nugetVersion + '"')}`);

      logger.box(lines, outcome.boxStyle);
      if (outcome.exitCode !== 0) {
        process.exitCode = outcome.exitCode;
      }
    }
  });
